import useSWR, { useSWRInfinite } from 'swr';
import {useState} from "react";
import { getFormattedPeriod } from '@utils/helpers';
import { fetcher, bffFetcher } from '@utils/api';

function useGetCampaignFolders(filter = {}, datePreset, code, periodParams) {
  const { appliedFilters, incentive_campaign_status, incentive_campaign_name = '' } = filter;
  const [allData, setAllData] = useState([])
  const body = {
    limit: '12',
    incentive_campaign_situation: 'with_incentive_campaign',
    fields: 'group_view',
    permitted_group: code,
    since_current: periodParams.start,
    until_current: periodParams.end,
    intersection_period: true,
    incentive_campaign_name: incentive_campaign_name,
  };
  if (appliedFilters) body.incentive_campaign_status = incentive_campaign_status;
  const uri = new URLSearchParams(body).toString();
  const url = `/api/v1/incentivecampaign/period?${uri}`;


  const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      const shouldFetchMore = allData.length < previousPageData?.payload?.count_total
      if ((previousPageData && !shouldFetchMore) || !periodParams?.start){
        return null;
      }
      if (pageIndex === 0) return url;

      return `${url}&skip=${body.limit * pageIndex}`;
    },
    bffFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const hasResults = Boolean(data?.length && data[0]?.status_code !== 'no_content');

  const fetchMoreData = () => {
    if (!isValidating) setSize((sizeNumber) => sizeNumber + 1);
  };

  const flattenPages = hasResults
    ? data.flatMap((page) => {
        if (page?.payload?.data) {
          return page.payload.data;
        }
        return [];
      })
    : [];

  if(allData.length !== flattenPages.length){
    setAllData(flattenPages)
  }

  const hasNextPage = Boolean(hasResults && flattenPages?.length !== data[0]?.payload?.count_total);

  const isLoading =
    (!data && !error) || (size > 0 && data && typeof data[size - 1] === 'undefined');

  return {
    campaigns: flattenPages,
    lastCampaign: data?.length ? data[0]?.payload?.last_campaign : null,
    error,
    fetchMoreData,
    hasNextPage,
    isLoading,
    mutate,
  };
}

function useMetrics(_filter = {}, datePreset) {
  const { since, sinceToCompare } = datePreset;

  const url = {
    since_current: since.startOfCurrentMonth,
    until_current: since.currentDayOfMonth,
    since_previous: sinceToCompare.startOfLastMonth,
    until_previous: sinceToCompare.currentDayOfLastMonth,
    limit: '12',
  };

  const { data, error, mutate } = useSWR(
    `/incentivecampaign/1.0/report/daterange/reportMultiplefilters?${new URLSearchParams(
      url
    ).toString()}`,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    metrics: data || null,
    isLoading: !error && !data,
    isError: error ? error.message : error,
    mutate,
  };
}

function usePerformanceMetrics(_filter = {}, datePreset, periodParams) {
  const { previousEnd, previousStart } = getFormattedPeriod(periodParams);

  const url = {
    since_current: periodParams.start,
    until_current: periodParams.end,
    since_previous: previousStart,
    until_previous: previousEnd,
    intersection_period: true,
  };

  const { data, error, mutate } = useSWR(
    periodParams.start
      ? `/api/v1/incentivecampaign/report/period?since_current=${url.since_current}&until_current=${url.until_current}&since_previous=${url.since_previous}&until_previous=${url.until_previous}&intersection_period=${url.intersection_period}`
      : null,
    bffFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    performanceMetrics: data?.payload || null,
    isLoading: !error && !data,
    isError: error ? error.message : error,
    mutate,
  };
}

function useFinancialMetrics(_filter = {}, datePreset, periodParams) {
  const { previousEnd, previousStart } = getFormattedPeriod(periodParams);

  const url = {
    since_current: periodParams.start,
    until_current: periodParams.end,
    since_previous: previousStart,
    until_previous: previousEnd,
    intersection_period: true,
  };

  const { data, error, mutate } = useSWR(
    periodParams.start
      ? `/api/v1/incentivecampaign/insights/period?since_current=${url.since_current}&until_current=${url.until_current}&since_previous=${url.since_previous}&until_previous=${url.until_previous}&intersection_period=${url.intersection_period}`
      : null,
    bffFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    financialMetrics: data?.payload || null,
    isLoading: !error && !data,
    isError: error ? error.message : error,
    mutate,
  };
}

function useGetTradePartners() {
  const { data, error, mutate } = useSWR(`/settings/1.0/keyvalue/trade_partner`, fetcher, {
    revalidateOnFocus: false,
  });

  return {
    data: data ? JSON.parse(data.value) : null,
    isLoading: !error && data === undefined,
    isError: error ? error.message : error,
    mutate: mutate,
  };
}

function useGetPermittedUnits(code, fields) {
  const {data, error} = useSWR(code ? `/schema/1.1/${code}/leaves?fields=${fields ? fields : ''}` : null, fetcher, {
    revalidateOnFocus: false,
  })
  return {
    data: data ? data : null,
    isLoading: !error && data === undefined,
    isError: error ? error.message : error,
  };
}

export {
  useGetCampaignFolders,
  useMetrics,
  usePerformanceMetrics,
  useFinancialMetrics,
  useGetTradePartners,
  useGetPermittedUnits
};
